import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useCurrentUserPermissions } from "../hooks/useCurrentUserPermissions";

export interface AdminGuardProps {
  /** Shown while the permissions load, e.g. the page's loading state. */
  fallback?: ReactNode;
}

/**
 * Route-level gate that limits access to users whose
 * `useCurrentUserPermissions()` returns `is_admin: true`. Non-admins
 * redirect to `/forbidden`. Pairs with `AuthGuard` — `AuthGuard`
 * resolves the session, `AdminGuard` checks the role.
 *
 * Renders `fallback` (default: nothing) while the permissions query is
 * in-flight, so a real admin isn't redirected away before
 * `/v2/auth/me/permissions` lands.
 */
export function AdminGuard({ fallback = null }: AdminGuardProps) {
  const { data, isLoading } = useCurrentUserPermissions();

  if (isLoading) return fallback;

  if (!data?.is_admin) return <Navigate to="/forbidden" replace />;

  return <Outlet />;
}
